import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from "@phosphor-icons/react";
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const INK = '#2A1E5C';
const INK2 = '#4A4458';
const MUTED = '#8B7E6A';
const GOLD = '#F5CB5C';
const CREAM = '#FDFBF7';
const SAND = '#F5F0E8';

const Eyebrow = ({ children, light = false }) => (
  <p className="font-body text-[11px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: light ? GOLD : MUTED }}>{children}</p>
);

const PLEDGES = [
  'Build useful software.',
  'Respect people’s data.',
  'Support independent work.',
  'Grow responsibly.',
];

const AboutPage = () => (
  <div className="min-h-screen" style={{ background: CREAM }} data-testid="about-page">
    <SiteNav />

    {/* Intro, mission first */}
    <section className="pt-14 pb-10 sm:pt-20 lg:pt-24 lg:pb-14 px-4 sm:px-6 lg:px-8" data-testid="about-intro">
      <div className="max-w-3xl mx-auto">
        <Eyebrow>About Payday Relay</Eyebrow>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] mb-6" style={{ color: INK }}>
          A weekly finish line for people who work for themselves.
        </h1>
        <p className="font-body text-lg sm:text-xl leading-relaxed mb-5" style={{ color: INK2 }}>
          Payday Relay turns unstructured 1099 income into a repeatable rhythm. It was built for independent earners
          whose work has no paycheck, no clear stopping point, and no one above telling them when they've worked
          enough to cover all their bases.
        </p>
        <p className="font-body text-lg sm:text-xl leading-relaxed font-medium" style={{ color: INK }}>
          Payday Relay gives that work a shape.
        </p>
      </div>
    </section>

    {/* Built From Lived Experience */}
    <section className="py-14 lg:py-20 px-4 sm:px-6 lg:px-8" style={{ background: SAND }} data-testid="about-lived-experience">
      <div className="max-w-3xl mx-auto">
        <Eyebrow>Built from lived experience</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-medium mb-6" style={{ color: INK }}>
          I built Payday Relay because I needed it.
        </h2>
        <div className="space-y-5 font-body text-base sm:text-lg leading-relaxed" style={{ color: INK2 }}>
          <p>
            I was diagnosed with inattentive-type ADD at 33, after decades of studying and working in systems that
            didn't work for me. So naturally, ADHD is part of Payday Relay's origin story. It's visual and gamified.
            Progress is immediate and easy to see, and big goals are broken into smaller manageable targets.
          </p>
          <p>
            But you don't have to have ADHD to benefit from Payday Relay. One of its biggest rewards is the momentum
            that comes from building on your own progress, one week at a time.
          </p>
          <p>
            Payday Relay was built for gig workers, freelancers, and independent service providers who have plenty of
            work available, but no reliable way to know when they've worked enough to cover not just their expenses,
            but taxes, savings goals, and a real vacation.
          </p>
          <p style={{ color: INK, fontWeight: 500 }}>
            Or, put more simply: when they've earned enough to confidently stop for the day.
          </p>
        </div>
      </div>
    </section>

    {/* The Founder */}
    <section className="py-14 lg:py-20 px-4 sm:px-6 lg:px-8" data-testid="about-founder">
      <div className="max-w-4xl mx-auto grid md:grid-cols-[minmax(0,260px)_1fr] gap-8 md:gap-12 items-center">
        <img
          src="/images/founder-dove.jpg"
          alt="Shari Dove Shepard, founder of Payday Relay"
          className="w-56 h-56 sm:w-64 sm:h-64 md:w-full md:h-auto md:aspect-square rounded-3xl object-cover shadow-xl mx-auto md:mx-0"
          style={{ border: `4px solid ${GOLD}` }}
          data-testid="about-founder-photo"
        />
        <div>
          <Eyebrow>The founder</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium mb-4" style={{ color: INK }}>
            I’m Shari “Dove” Shepard.
          </h2>
          <div className="space-y-4 font-body text-base sm:text-lg leading-relaxed" style={{ color: INK2 }}>
            <p>
              I built Payday Relay after years of remote 1099 work where the freedom to work whenever I wanted came
              with a problem: I never knew when I’d done enough.
            </p>
            <p>
              That became impossible to ignore after I moved to Mexico and suddenly had a very concrete income
              target to hit. I needed a way to turn a big number into something I could work toward today.
            </p>
            <p style={{ color: INK, fontWeight: 500 }}>
              As Payday Relay’s first long-term user, I still run my own 52-week relay race on the same system my
              users get.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Built With Care */}
    <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8" style={{ background: INK }} data-testid="founder-pledge">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium mb-6 leading-tight" style={{ color: '#fff' }} data-testid="founder-pledge-heading">
          Ownership and Integrity
        </h2>
        <div className="space-y-5 font-body text-base sm:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>
          <p>
            Payday Relay is a privacy-conscious, values-led tool built specifically for independent earners. We
            stick to clear pricing, plain language, and honest AI use, with absolutely no dark-pattern conversion
            tricks. Your numbers belong entirely to you. Your Google Sheet lives in your own Drive, and our system
            only accesses it to run the calculations, never to sell your data or turn your financial life into
            someone else’s asset.
          </p>
          <p>
            Please note: Caddy, our in-app coach, is here to support reflection and clarity, but does not replace
            professional financial, legal, tax, or investment advice.
          </p>
          <p>
            Long term, Payday Relay is building toward formal social and environmental accountability through paths
            like B Corp certification and 1% for the Planet.
          </p>
          <p style={{ color: '#fff' }}>For now, the pledge is simple:</p>
        </div>

        <ul className="mt-6 rounded-3xl p-6 sm:p-7 space-y-2 font-heading text-lg sm:text-xl" style={{ background: GOLD, color: INK }} data-testid="founder-pledge-list">
          {PLEDGES.map((line) => (
            <li key={line} className="flex gap-3">
              <span aria-hidden="true">→</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8" data-testid="about-cta">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-medium mb-4" style={{ color: INK }}>
          Start Your Free 4-Relay Sprint
        </h2>
        <p className="font-body text-base sm:text-lg leading-relaxed mb-8" style={{ color: INK2 }}>
          Run four weekly relays with your real numbers and your own Google Sheet.
          <br className="hidden sm:block" />
          See what changes when your workweek has a finish line.
        </p>
        <Link
          to="/start/free-sprint"
          className="inline-flex items-center gap-3 px-10 py-5 rounded-full font-heading font-bold text-lg transition-transform hover:scale-105"
          style={{ background: GOLD, color: INK, boxShadow: '0 8px 30px rgba(245, 203, 92, 0.4)' }}
          data-testid="about-cta-button"
        >
          Start Your Free 4-Relay Sprint
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>

    <SiteFooter />
  </div>
);

export default AboutPage;
